// Kho lấy hàng — nhiều kho (GĐ3). Dùng chung cho Cài đặt › Kho lấy hàng và màn
// "Hoàn tất thông tin tài khoản". Tài khoản BEST là tài khoản chung của ShopTrack;
// đơn vị chỉ khai kho. Mỗi đơn chọn một kho (mặc định = kho đánh dấu mặc định).
// Kho đã dùng chỉ ngừng dùng, không xoá.

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { get, post, put } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { formatPhoneVN } from '@/lib/format'
import { invalidateProfileStatus } from '@/lib/onboarding'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { confirm } from '@/components/ui/confirm'
import { IndexBadge } from '@/components/ui/data-table'
import {
  WarehouseFormFields,
  emptyWarehouseForm,
  fullAddress,
  warehousePayload,
  warehouseToForm,
  type Warehouse,
  type WarehouseFormValue,
} from './WarehouseForm'

export type { Warehouse } from './WarehouseForm'

/** Đủ thông tin để ShopTrack coi là kho lấy hàng hợp lệ (cùng điều kiện với GET /onboarding/required). */
export function isWarehouseComplete(w: Pick<Warehouse, 'contactName' | 'contactPhone' | 'address' | 'city' | 'ward'>) {
  return [w.contactName, w.contactPhone, w.address, w.city, w.ward].every((v) => !!v?.trim())
}

export function WarehouseManager({
  intro = true,
  readOnly = false,
}: {
  /** Tiêu đề + mô tả đầu khối (Cài đặt). Màn hoàn tất thông tin tự có tiêu đề bước. */
  intro?: boolean
  /** Chỉ xem — thành viên không có quyền sửa kho. */
  readOnly?: boolean
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => get<{ warehouses: Warehouse[] }>('/settings/warehouses'),
  })
  const warehouses = data?.warehouses ?? []
  const auth = getAuthState()
  const currentShop = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const defaultContact = currentShop?.name || auth.email || ''
  const defaultPhone = auth.phone || ''

  // null = đóng form; 'new' = thêm kho; số = sửa kho đó
  const [editing, setEditing] = useState<'new' | number | null>(null)
  const [form, setForm] = useState<WarehouseFormValue>(() => emptyWarehouseForm(defaultContact, defaultPhone, true))

  useEffect(() => {
    if (!readOnly && !isLoading && warehouses.length === 0 && editing === null) {
      setForm(emptyWarehouseForm(defaultContact, defaultPhone, true))
      setEditing('new')
    }
  }, [readOnly, isLoading, warehouses.length, editing, defaultContact, defaultPhone])

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['warehouses'] })
    void invalidateProfileStatus()
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = warehousePayload(form)
      return editing === 'new'
        ? post<{ warehouse: Warehouse }>('/settings/warehouses', payload)
        : put<{ warehouse: Warehouse }>(`/settings/warehouses/${editing}`, payload)
    },
    onSuccess: () => {
      toast.success(editing === 'new' ? 'Đã thêm kho lấy hàng' : 'Đã lưu kho lấy hàng')
      setEditing(null)
      refresh()
    },
    onError: (e) => toast.error((e as Error).message),
  })

  const setDefault = useMutation({
    mutationFn: (id: number) => post(`/settings/warehouses/${id}/default`, {}),
    onSuccess: () => {
      toast.success('Đã đổi kho mặc định')
      refresh()
    },
    onError: (e) => toast.error((e as Error).message),
  })

  const deactivate = useMutation({
    mutationFn: (id: number) => post(`/settings/warehouses/${id}/deactivate`, {}),
    onSuccess: () => {
      toast.success('Đã ngừng dùng kho')
      refresh()
    },
    onError: (e) => toast.error((e as Error).message),
  })

  const askDeactivate = async (w: Warehouse) => {
    const ok = await confirm({
      title: `Ngừng dùng "${w.name}"?`,
      description: 'Đơn mới không chọn được kho này nữa.',
      confirmLabel: 'Ngừng dùng',
      cancelLabel: 'Hủy',
    })
    if (ok) deactivate.mutate(w.id)
  }

  const startNew = () => {
    setForm(emptyWarehouseForm(defaultContact, defaultPhone, warehouses.length === 0))
    setEditing('new')
  }

  const addButton = !readOnly && editing === null && <Button onClick={startNew}>Thêm kho</Button>

  return (
    <div className="space-y-6">
      {intro ? (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl text-ink">Kho lấy hàng</h2>
          </div>
          {addButton}
        </div>
      ) : (
        warehouses.length > 0 && addButton && <div className="flex justify-end">{addButton}</div>
      )}

      {isLoading ? (
        <div className="p-8 text-center text-sm text-muted">Đang tải danh sách kho…</div>
      ) : warehouses.length === 0 ? (
        intro || readOnly ? (
          <div className="rounded-lg border border-warn/30 bg-warn/5 p-4 text-sm text-inkSoft">
            Chưa có kho lấy hàng nào.
          </div>
        ) : null
      ) : (
        <ul className="space-y-2">
          {warehouses.map((w, i) => {
            const complete = isWarehouseComplete(w)
            return (
              <li key={w.id} className={cn('rounded-lg border bg-raise p-4', !complete ? 'border-warn/40' : w.isDefault ? 'border-accent/40' : 'border-rule')}>
                <div className="flex items-start gap-3 flex-wrap">
                  {/* min-w 12rem: màn hẹp thì hàng nút xuống dưới — không có thì 3 nút chiếm chỗ, tên + địa chỉ bị ép
                      thành cột 1 chữ */}
                  <div className="flex-1 min-w-[12rem] space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <IndexBadge n={i + 1} />
                      <p className="font-medium text-ink">{w.name}</p>
                      {w.isDefault && <span className="text-xs px-1.5 py-0.5 rounded bg-accent/12 text-accent">Mặc định</span>}
                      {!complete && <span className="text-xs px-1.5 py-0.5 rounded bg-warn/12 text-warn">Thiếu thông tin</span>}
                    </div>
                    <p className="text-sm text-inkSoft">
                      {w.contactName || 'Chưa có người liên hệ'} · {w.contactPhone ? formatPhoneVN(w.contactPhone) : 'chưa có SĐT'}
                    </p>
                    <p className="text-sm text-inkSoft break-words">{fullAddress(w.address ?? '', w.ward ?? '', w.district, w.city ?? '') || 'Chưa có địa chỉ'}</p>
                    {!w.returnSameAsPickup && (
                      <p className="text-xs text-muted break-words">
                        Hoàn hàng về: {fullAddress(w.returnAddress ?? '', w.returnWard ?? '', w.returnDistrict, w.returnCity ?? '')}
                      </p>
                    )}
                  </div>
                  {!readOnly && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {!w.isDefault && (
                        <Button variant="outline" size="sm" disabled={setDefault.isPending} onClick={() => setDefault.mutate(w.id)}>
                          Đặt mặc định
                        </Button>
                      )}
                      <Button variant={complete ? 'outline' : 'default'} size="sm" onClick={() => { setForm(warehouseToForm(w)); setEditing(w.id) }}>
                        {complete ? 'Sửa' : 'Bổ sung'}
                      </Button>
                      {warehouses.length > 1 && (
                        <Button variant="ghost" size="sm" disabled={deactivate.isPending} onClick={() => askDeactivate(w)}>
                          Ngừng dùng
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {!readOnly && editing !== null && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
          className={cn('space-y-4', intro ? 'rounded-xl p-6 bg-raise shadow-card' : warehouses.length > 0 && 'rounded-lg border border-rule p-4')}
        >
          {(intro || warehouses.length > 0) && (
            <h3 className="text-base font-medium text-ink">{editing === 'new' ? 'Thêm kho' : 'Sửa kho'}</h3>
          )}
          <WarehouseFormFields form={form} setForm={setForm} disabled={save.isPending} showDefault={warehouses.length > 0} />

          <div className="pt-2 flex items-center gap-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Đang lưu…' : editing === 'new' ? 'Thêm kho' : 'Lưu'}
            </Button>
            {warehouses.length > 0 && (
              <Button type="button" variant="ghost" onClick={() => setEditing(null)} disabled={save.isPending}>
                Hủy
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  )
}
